import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Dashboard from '@/pages/Dashboard';
import Contacts from '@/pages/Contacts';
import Campaigns from '@/pages/Campaigns';
import Templates from '@/pages/Templates';
import Exclusions from '@/pages/Exclusions';
import Analytics from '@/pages/Analytics';
import Hotels from '@/pages/Hotels';
import Login from '@/pages/Login';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import CookiesPolicy from '@/pages/CookiesPolicy';
import LegalNotice from '@/pages/LegalNotice';
import Unsubscribe from '@/pages/Unsubscribe';
import CookieBanner from '@/components/CookieBanner';
import Footer from '@/components/Footer';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { Mail, Users, Hotel, FileText, LayoutDashboard, Menu, X, LogOut, UserX, ExternalLink, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const EDITOR_PASSWORD = 'hotel2024';

const handleEditarWebClick = (e) => {
  e.preventDefault();
  const pwd = window.prompt('Introduce la contraseña para editar la web:');
  if (pwd === null) return;
  if (pwd === EDITOR_PASSWORD) {
    window.open('https://app.emergent.sh/chat', '_blank', 'noopener,noreferrer');
  } else {
    toast.error('Contraseña incorrecta');
  }
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const Navigation = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Don't show navigation on login page or public home page
  if (location.pathname === '/login' || location.pathname === '/' || location.pathname === '/baja') {
    return null;
  }
  
  // Don't show navigation if not logged in
  if (!user) {
    return null;
  }
  
  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/contacts', label: 'Contactos', icon: Users },
    { path: '/admin/campaigns', label: 'Campañas', icon: Mail },
    { path: '/admin/templates', label: 'Plantillas', icon: FileText },
    { path: '/admin/analytics', label: 'Analíticas', icon: BarChart3 },
    { path: '/admin/exclusions', label: 'Exclusiones', icon: UserX },
    { path: '/', label: 'Ver Web Pública', icon: Hotel },
  ];
  
  const handleLogout = () => {
    logout();
  };
  
  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Hotel className="h-8 w-8 text-blue-600" />
            <span className="ml-2 text-xl font-bold text-gray-900">HotelMail Pro</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Salir
            </Button>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block px-3 py-2 rounded-md text-base font-medium flex items-center gap-2 ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
            <button
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              data-testid="mobile-logout-button"
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium flex items-center gap-2 text-red-600 hover:bg-red-50"
            >
              <LogOut className="h-5 w-5" />
              Salir
            </button>
            <a
              href="https://app.emergent.sh/chat"
              onClick={handleEditarWebClick}
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium flex items-center gap-2 text-emerald-600 hover:bg-emerald-50"
              data-testid="mobile-emergent-editor-button"
            >
              <ExternalLink className="h-5 w-5" />
              Editar Web
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();

  // Remove Emergent badge
  useEffect(() => {
    const removeBadge = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
    };
    removeBadge();
    const interval = setInterval(removeBadge, 300);
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { clearInterval(interval); observer.disconnect(); };
  }, []);

  // Inject Google Analytics 4 if Measurement ID is configured
  useEffect(() => {
    const GA4_ID = process.env.REACT_APP_GA4_ID;
    if (!GA4_ID || document.getElementById('ga4-script')) return;

    const s1 = document.createElement('script');
    s1.id = 'ga4-script';
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s1);

    const s2 = document.createElement('script');
    s2.id = 'ga4-init';
    s2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA4_ID}', { anonymize_ip: true });
    `;
    document.head.appendChild(s2);
  }, []);

  // Track route changes in GA4
  useEffect(() => {
    if (window.gtag && process.env.REACT_APP_GA4_ID) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
  
  // Show footer only on public pages
  const showFooter = ['/', '/privacidad', '/cookies', '/aviso-legal', '/baja'].includes(location.pathname);
  
  return (
    <div className="App min-h-screen bg-gray-50 flex flex-col">
      <Navigation />
      {user && location.pathname.startsWith('/admin') && (
        <a
          href="https://app.emergent.sh/chat"
          onClick={handleEditarWebClick}
          className="fixed bottom-5 left-5 z-50 flex items-center gap-2 bg-emerald-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-emerald-700 transition-colors cursor-pointer"
          data-testid="emergent-editor-button"
        >
          <ExternalLink className="h-4 w-4" />
          <span className="text-sm font-medium">Editar Web</span>
        </a>
      )}
      <main className={user && !showFooter ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow" : "flex-grow"}>
        <Routes>
          {/* Public route - Hotels landing page as home */}
          <Route path="/" element={<Hotels />} />
          
          {/* Login route */}
          <Route path="/login" element={<Login />} />
          
          {/* Legal pages - Public */}
          <Route path="/privacidad" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/aviso-legal" element={<LegalNotice />} />
          <Route path="/baja" element={<Unsubscribe />} />
          
          {/* Protected routes - Admin panel */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/contacts" element={
            <ProtectedRoute>
              <Contacts />
            </ProtectedRoute>
          } />
          <Route path="/admin/campaigns" element={
            <ProtectedRoute>
              <Campaigns />
            </ProtectedRoute>
          } />
          <Route path="/admin/templates" element={
            <ProtectedRoute>
              <Templates />
            </ProtectedRoute>
          } />
          <Route path="/admin/exclusions" element={
            <ProtectedRoute>
              <Exclusions />
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          } />
          
          {/* Redirects for old URLs */}
          <Route path="/hoteles" element={<Navigate to="/" replace />} />
          <Route path="/hotels" element={<Navigate to="/" replace />} />
          <Route path="/politica-privacidad" element={<Navigate to="/privacidad" replace />} />
          <Route path="/politica-cookies" element={<Navigate to="/cookies" replace />} />
          <Route path="/contacts" element={<Navigate to="/admin/contacts" replace />} />
          <Route path="/campaigns" element={<Navigate to="/admin/campaigns" replace />} />
          <Route path="/templates" element={<Navigate to="/admin/templates" replace />} />
          <Route path="/exclusions" element={<Navigate to="/admin/exclusions" replace />} />
        </Routes>
      </main>
      {showFooter && <Footer />}
      <CookieBanner />
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
